import { useState, useEffect, useCallback } from 'react';

const DB_NAME = 'BanyanAIBotDB';
const DB_VERSION = 3;
const STORE_NAME = 'processedItems';
const NOUN_MODIFIER_STORE = 'nounModifierData';
const INC_DATA_STORE = 'incData';

/** Initialize IndexedDB */
const initDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
            const db = request.result;
            // Close connection if a version change (like delete) is requested
            // This allows other tabs/components to close their connections so delete can proceed
            db.onversionchange = () => {
                db.close();
                console.log('Database version changed, connection closed');
            };
            resolve(db);
        };
        request.onupgradeneeded = (event) => {
            const db = event.target.result;

            // Create processedItems store if it doesn't exist
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const objectStore = db.createObjectStore(STORE_NAME, {
                    keyPath: 'id',
                    autoIncrement: true,
                });
                objectStore.createIndex('manufacturerNumber', 'manufacturerNumber', { unique: false });
                objectStore.createIndex('status', 'status', { unique: false });
                objectStore.createIndex('createdAt', 'createdAt', { unique: false });
            }

            // Create nounModifierData store if it doesn't exist
            if (!db.objectStoreNames.contains(NOUN_MODIFIER_STORE)) {
                const nounModStore = db.createObjectStore(NOUN_MODIFIER_STORE, {
                    keyPath: 'id',
                    autoIncrement: true,
                });
                nounModStore.createIndex('partnum', 'partnum', { unique: false });
                nounModStore.createIndex('createdAt', 'createdAt', { unique: false });
            }

            // Create incData store if it doesn't exist
            if (!db.objectStoreNames.contains(INC_DATA_STORE)) {
                const incDataStore = db.createObjectStore(INC_DATA_STORE, {
                    keyPath: 'id',
                    autoIncrement: true,
                });
                incDataStore.createIndex('escn', 'escn', { unique: false });
                incDataStore.createIndex('createdAt', 'createdAt', { unique: false });
            }

        };
    });
};

/** Utility to format bytes */
const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const useIndexedDB = () => {
    const [db, setDb] = useState(null);
    const [dbSize, setDbSize] = useState(0);
    const [loading, setLoading] = useState(true);
    const [itemCount, setItemCount] = useState(0);

    // Initialize DB on mount
    useEffect(() => {
        let isMounted = true;
        initDB()
            .then((database) => {
                if (isMounted) {
                    setDb(database);
                    setLoading(false);
                    calculateDbSize();
                    calculateItemCount(database);
                } else {
                    database.close();
                }
            })
            .catch((error) => {
                console.error('Error initializing IndexedDB:', error);
                if (isMounted) setLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    /** Calculate storage usage */
    const calculateDbSize = useCallback(async () => {
        if ('storage' in navigator && 'estimate' in navigator.storage) {
            const estimate = await navigator.storage.estimate();
            setDbSize(estimate.usage || 0);
        }
    }, []);

    /** Calculate item count */
    const calculateItemCount = useCallback(async (database = db) => {
        if (!database) return;
        try {
            const tx = database.transaction([STORE_NAME], 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const req = store.count();
            req.onsuccess = () => {
                setItemCount(req.result);
            };
        } catch (e) {
            console.error("Error counting items", e);
        }
    }, [db]);

    /** Add a single item */
    const addItem = useCallback(
        async (item) => {
            if (!db) return null;
            return new Promise((resolve, reject) => {
                const tx = db.transaction([STORE_NAME], 'readwrite');
                const store = tx.objectStore(STORE_NAME);
                const payload = {
                    ...item,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                };
                const request = store.add(payload);
                request.onsuccess = () => {
                    calculateDbSize();
                    calculateItemCount();
                    resolve(request.result);
                };
                request.onerror = () => reject(request.error);
            });
        },
        [db, calculateDbSize, calculateItemCount]
    );

    /** Add multiple items */
    const addItems = useCallback(
        async (items) => {
            if (!db) return [];
            return new Promise((resolve, reject) => {
                const tx = db.transaction([STORE_NAME], 'readwrite');
                const store = tx.objectStore(STORE_NAME);
                const results = [];
                items.forEach((item) => {
                    const payload = {
                        ...item,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    };
                    const req = store.add(payload);
                    req.onsuccess = () => results.push(req.result);
                });
                tx.oncomplete = () => {
                    calculateDbSize();
                    calculateItemCount();
                    resolve(results);
                };
                tx.onerror = () => reject(tx.error);
            });
        },
        [db, calculateDbSize, calculateItemCount]
    );

    /** Get all items */
    const getAllItems = useCallback(async () => {
        if (!db) return [];
        return new Promise((resolve, reject) => {
            const tx = db.transaction([STORE_NAME], 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }, [db]);

    /** Get items by status */
    const getItemsByStatus = useCallback(async (status) => {
        if (!db) return [];
        return new Promise((resolve, reject) => {
            const tx = db.transaction([STORE_NAME], 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const index = store.index('status');
            const request = index.getAll(status);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }, [db]);

    /** Update an item */
    const updateItem = useCallback(async (id, updates) => {
        if (!db) return null;
        return new Promise((resolve, reject) => {
            const tx = db.transaction([STORE_NAME], 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const getReq = store.get(id);
            getReq.onsuccess = () => {
                const item = getReq.result;
                if (!item) {
                    reject(new Error('Item not found'));
                    return;
                }
                const updated = { ...item, ...updates, updatedAt: new Date().toISOString() };
                const putReq = store.put(updated);
                putReq.onsuccess = () => {
                    calculateDbSize();
                    // Item count doesn't change on update
                    resolve(putReq.result);
                };
                putReq.onerror = () => reject(putReq.error);
            };
            getReq.onerror = () => reject(getReq.error);
        });
    }, [db, calculateDbSize]);

    /** Delete an item */
    const deleteItem = useCallback(async (id) => {
        if (!db) return;
        return new Promise((resolve, reject) => {
            const tx = db.transaction([STORE_NAME], 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const req = store.delete(id);
            req.onsuccess = () => {
                calculateDbSize();
                calculateItemCount();
                resolve();
            };
            req.onerror = () => reject(req.error);
        });
    }, [db, calculateDbSize, calculateItemCount]);

    /** Clear all data by deleting the database */
    const clearAll = useCallback(async () => {
        if (!db) return;

        // Close the current connection to avoid blocking
        db.close();
        setDb(null);

        return new Promise((resolve, reject) => {
            const request = indexedDB.deleteDatabase(DB_NAME);

            request.onsuccess = async () => {
                setDbSize(0);
                setItemCount(0);
                resolve();
            };

            request.onerror = () => reject(request.error);

            request.onblocked = () => {
                console.warn('Delete database blocked - waiting for other connections to close');
            };
        });
    }, [db]);

    /** Add Noun/Modifier data */
    const addNounModifierData = useCallback(
        async (data) => {
            if (!db) return [];
            return new Promise((resolve, reject) => {
                const tx = db.transaction([NOUN_MODIFIER_STORE], 'readwrite');
                const store = tx.objectStore(NOUN_MODIFIER_STORE);
                const results = [];

                data.forEach((item) => {
                    const payload = {
                        ...item,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    };
                    const req = store.add(payload);
                    req.onsuccess = () => results.push(req.result);
                });

                tx.oncomplete = () => {
                    calculateDbSize();
                    resolve(results);
                };
                tx.onerror = () => reject(tx.error);
            });
        },
        [db, calculateDbSize]
    );

    /** Get all Noun/Modifier data */
    const getAllNounModifiers = useCallback(async () => {
        if (!db) return [];
        return new Promise((resolve, reject) => {
            const tx = db.transaction([NOUN_MODIFIER_STORE], 'readonly');
            const store = tx.objectStore(NOUN_MODIFIER_STORE);
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }, [db]);

    /** Clear Noun/Modifier data */
    const clearNounModifiers = useCallback(async () => {
        if (!db) return;
        return new Promise((resolve, reject) => {
            const tx = db.transaction([NOUN_MODIFIER_STORE], 'readwrite');
            const store = tx.objectStore(NOUN_MODIFIER_STORE);
            const request = store.clear();
            request.onsuccess = () => {
                calculateDbSize();
                resolve();
            };
            request.onerror = () => reject(request.error);
        });
    }, [db, calculateDbSize]);

    /** Add INC Data */
    const addIncData = useCallback(
        async (data) => {
            if (!db) return [];
            return new Promise((resolve, reject) => {
                const tx = db.transaction([INC_DATA_STORE], 'readwrite');
                const store = tx.objectStore(INC_DATA_STORE);
                const results = [];

                data.forEach((item) => {
                    const payload = {
                        ...item,
                        createdAt: new Date().toISOString(),
                        updatedAt: new Date().toISOString(),
                    };
                    const req = store.add(payload);
                    req.onsuccess = () => results.push(req.result);
                });

                tx.oncomplete = () => {
                    calculateDbSize();
                    resolve(results);
                };
                tx.onerror = () => reject(tx.error);
            });
        },
        [db, calculateDbSize]
    );

    /** Get all INC Data */
    const getAllIncData = useCallback(async () => {
        if (!db) return [];
        return new Promise((resolve, reject) => {
            const tx = db.transaction([INC_DATA_STORE], 'readonly');
            const store = tx.objectStore(INC_DATA_STORE);
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }, [db]);

    /** Get INC Data by ESCN */
    const getIncDataByEscn = useCallback(async (escn) => {
        if (!db) return [];
        return new Promise((resolve, reject) => {
            const tx = db.transaction([INC_DATA_STORE], 'readonly');
            const store = tx.objectStore(INC_DATA_STORE);
            const index = store.index('escn');
            const request = index.getAll(escn);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }, [db]);

    /** Get Noun/Modifier by partnum */
    const getNounModifierByPartnum = useCallback(async (partnum) => {
        if (!db) return null;
        return new Promise((resolve, reject) => {
            const tx = db.transaction([NOUN_MODIFIER_STORE], 'readonly');
            const store = tx.objectStore(NOUN_MODIFIER_STORE);
            const index = store.index('partnum');
            const request = index.get(partnum);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }, [db]);

    /** Clear INC Data */

    const clearIncData = useCallback(async () => {
        if (!db) return;
        return new Promise((resolve, reject) => {
            const tx = db.transaction([INC_DATA_STORE], 'readwrite');
            const store = tx.objectStore(INC_DATA_STORE);
            const request = store.clear();
            request.onsuccess = () => {
                calculateDbSize();
                resolve();
            };
            request.onerror = () => reject(request.error);
        });
    }, [db, calculateDbSize]);

    return {
        loading,
        dbSize,
        itemCount,
        formattedDbSize: formatBytes(dbSize),
        addItem,
        addItems,
        getAllItems,
        getItemsByStatus,
        updateItem,
        deleteItem,
        clearAll,
        // Noun/Modifier operations
        addNounModifierData,
        getAllNounModifiers,
        clearNounModifiers,
        // INC Data operations
        addIncData,
        getAllIncData,
        getIncDataByEscn,
        clearIncData,
        // Noun/Modifier lookup
        getNounModifierByPartnum,

        refreshSize: () => {
            calculateDbSize();
            calculateItemCount();
        },
    };
};
